import { Download, Heart, ListMusic, Mic2, Pause, Play, Repeat, SkipBack, SkipForward, Volume2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlayer } from "../context/PlayerContext";
import { downloadUrl, http } from "../lib/api";
import { Cover } from "./Cover";
import { KaraokeLyrics } from "./Karaoke";
import { AddToPlaylist } from "./AddToPlaylist";
import { Waveform } from "./Waveform";

export const fmt = (s) => (!s || isNaN(s) ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

const IconBtn = ({ children, testId, onClick, active, className = "" }) => (
  <button data-testid={testId} onClick={onClick}
    className={`p-2 rounded-full transition-colors duration-200 hover:bg-[var(--c-hover)] ${active ? "text-[var(--c-orange)]" : "text-zinc-300 hover:text-white"} ${className}`}>
    {children}
  </button>
);

const LyricsPanel = () => {
  const { current, setLyricsOpen } = usePlayer();
  return (
    <div data-testid="player-lyrics-panel" className="fixed right-4 bottom-28 z-50 w-[min(380px,calc(100vw-2rem))] max-h-[60vh] flex flex-col rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)]/95 backdrop-blur-xl shadow-2xl rise">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--c-line)]">
        <span className="font-display text-sm">Žodžiai</span>
        <IconBtn testId="player-lyrics-close" onClick={() => setLyricsOpen(false)}><X className="w-4 h-4" /></IconBtn>
      </div>
      <KaraokeLyrics song={current} className="px-5 py-4 h-[50vh]" />
    </div>
  );
};

export const AudioPlayer = () => {
  const p = usePlayer();
  const { current } = p;
  if (!current) return null;
  const like = async () => !current.id.endsWith("-src") && p.updateCurrent((await http.post(`/songs/${current.id}/like`)).data);
  return (
    <>
      {p.lyricsOpen && <LyricsPanel />}
      <div data-testid="audio-player" className="fixed bottom-0 inset-x-0 z-50 border-t border-[var(--c-line)] bg-[#0d0d12]/90 backdrop-blur-2xl">
        <div data-testid="player-mobile-progress" className="md:hidden h-1.5 bg-[var(--c-elev)] cursor-pointer"
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); p.seek(((e.clientX - r.left) / r.width) * p.duration); }}>
          <div className="h-full bg-[var(--c-orange)]" style={{ width: `${p.duration ? (p.time / p.duration) * 100 : 0}%` }} />
        </div>
        <div className="flex items-center gap-4 px-4 md:px-6 h-24">
          <Link to={`/daina/${current.id}`} className="flex items-center gap-3 w-56 min-w-0 shrink-0" data-testid="player-track-link">
            <Cover song={current} className="w-14 h-14 rounded-lg" />
            <div className="min-w-0">
              <p data-testid="player-title" className="text-sm font-semibold truncate">{current.title}</p>
              <p className="text-xs text-zinc-500 truncate">{current.author_name}</p>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <IconBtn testId="player-prev" onClick={p.prev} className="hidden sm:block"><SkipBack className="w-4 h-4" /></IconBtn>
            <button data-testid="audio-player-play-button" onClick={p.toggle}
              className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform duration-150">
              {p.playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
            </button>
            <IconBtn testId="player-next" onClick={p.next} className="hidden sm:block"><SkipForward className="w-4 h-4" /></IconBtn>
          </div>
          <div className="hidden md:flex flex-1 items-center gap-3 min-w-0">
            <span className="text-xs font-mono text-zinc-500 w-10 text-right" data-testid="player-time">{fmt(p.time)}</span>
            <Waveform testId="player-waveform" seed={current.id} progress={p.duration ? p.time / p.duration : 0}
              onSeek={(f) => p.seek(f * p.duration)} className="h-9 flex-1" n={120} />
            <span className="text-xs font-mono text-zinc-500 w-10" data-testid="player-duration">{fmt(p.duration)}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto md:ml-0">
            <IconBtn testId="player-like" onClick={like} active={current.liked}><Heart className="w-4 h-4" fill={current.liked ? "currentColor" : "none"} /></IconBtn>
            {!current.id.endsWith("-src") && <AddToPlaylist song={current} className="p-2 rounded-full text-zinc-300 hover:text-white hover:bg-[var(--c-hover)] hidden sm:block" />}
            <span title={p.autoNext ? "Pasibaigus – groti kitą dainą" : "Pasibaigus – sustoti"}>
              <IconBtn testId="player-autonext" onClick={() => p.setAutoNext(!p.autoNext)} active={p.autoNext}><ListMusic className="w-4 h-4" /></IconBtn>
            </span>
            <span title={p.repeat ? "Kartoti šią dainą: įjungta" : "Kartoti šią dainą: išjungta"}>
              <IconBtn testId="player-repeat" onClick={() => p.setRepeat(!p.repeat)} active={p.repeat}><Repeat className="w-4 h-4" /></IconBtn>
            </span>
            <IconBtn testId="player-lyrics-toggle" onClick={() => p.setLyricsOpen(!p.lyricsOpen)} active={p.lyricsOpen}><Mic2 className="w-4 h-4" /></IconBtn>
            <a href={downloadUrl(current)} data-testid="player-download" className="p-2 rounded-full text-zinc-300 hover:text-white hover:bg-[var(--c-hover)] hidden sm:block"><Download className="w-4 h-4" /></a>
            <div className="hidden lg:flex items-center gap-2 ml-2">
              <Volume2 className="w-4 h-4 text-zinc-400" />
              <input data-testid="player-volume" type="range" min="0" max="1" step="0.01" value={p.volume}
                onChange={(e) => p.setVolume(+e.target.value)} className="vol w-24" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
